/**
 * VaultDetailController — page /coffre/{id} (EPIC-035 / 35.5, spec §24.E/F).
 *
 * Trois modes UI selon `isImmutable` + `status` :
 *   - auto              (isImmutable=true)               → lecture seule + lien source primaire
 *   - manual_active     (isImmutable=false, active)      → formulaire éditable + archive
 *   - manual_archived   (isImmutable=false, archived)    → lecture seule + badge « Archivé le … »
 *
 * Endpoints consommés :
 *   - GET   /v1/vault/{id}             → chargement document + links.sourcePrimary
 *   - PATCH /v1/vault/{id}             → sauvegarde métadonnées (mode manual_active)
 *   - POST  /v1/vault/{id}/archive     → archivage soft-delete
 *   - GET   /v1/vault/{id}/download    → redirect 302 (URL signée 15 min)
 *
 * Conventions :
 *   - 0 HTML inline (templates + cloneTemplate + fillSlots).
 *   - Aucune classe inventée — toutes ⊆ notamicro.css.
 *   - Vocabulaire UI : « Plateforme Agréée (PA) », pas « PDP », pas NotaFacto.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const SIZE_UNITS = ["o", "Ko", "Mo", "Go"];

const DATE_LONG_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const DATE_SHORT_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const MANUAL_CATEGORY_LABELS = {
  kbis: "KBIS",
  avis_situation_insee: "Avis de situation INSEE",
  attestation_urssaf: "Attestation URSSAF",
  attestation_fiscale: "Attestation fiscale",
  avis_cfe: "Avis CFE",
  contrat: "Contrat",
  bail: "Bail",
  courrier_administratif: "Courrier administratif",
  assurance: "Assurance",
  banque: "Banque",
  autre: "Autre",
};

const AUTO_CATEGORY_LABELS = {
  purchase_register: "Registre des achats (annuel)",
  revenue_register: "Livre des recettes (annuel)",
  urssaf_filing: "Déclaration URSSAF",
  vat_filing: "Déclaration TVA",
  das2_filing: "Déclaration DAS2",
  ir_filing: "Déclaration de revenus",
};

const SOURCE_PRIMARY_LABELS = {
  purchase_register: (fy) => `Voir le registre des achats — exercice ${fy ?? ""}`.trim(),
  revenue_register: (fy) => `Voir le livre des recettes — exercice ${fy ?? ""}`.trim(),
  urssaf_filing: () => "Voir la déclaration URSSAF",
  vat_filing: () => "Voir la déclaration TVA",
  das2_filing: () => "Voir la déclaration DAS2",
  ir_filing: () => "Voir la déclaration de revenus",
};

const ARCHIVE_WARNING_MESSAGE =
  "Si ce document est requis légalement (10 ans), assurez-vous d'en conserver une copie ou de le ré-importer ultérieurement.";

export default class VaultDetailController extends BaseController {
  static targets = [
    "crumbTitle",
    "loading",
    "error",
    "content",
    "pdfViewer",
    "sourcePrimaryLink",
    "autoTitle",
    "autoCategory",
    "autoFiscalYear",
    "autoDocumentDate",
    "manualActiveTitle",
    "form",
    "title",
    "category",
    "fiscalYear",
    "documentDate",
    "notes",
    "saveBtn",
    "archivedTitle",
    "archivedCategory",
    "archivedFiscalYear",
    "archivedDocumentDate",
    "archivedNotes",
  ];

  _id = null;
  _document = null;
  _links = null;
  _originalFormValues = null;

  connect() {
    // Forme canonique (EPIC-099.53) : coffre-detail.html?id={uuid}
    const id = new URLSearchParams(window.location.search).get("id");
    if (!id) {
      this._showError("Document introuvable.");
      return;
    }
    this._id = id;
    this._loadDocument();
  }

  // ---------------------------------------------------------------------------
  // Chargement
  // ---------------------------------------------------------------------------

  async _loadDocument() {
    try {
      const resp = await api.vault.get(this._id);
      const doc = resp?.data || null;
      const links = resp?.links || {};
      if (!doc) {
        this._showError("Document introuvable.");
        return;
      }
      this._document = doc;
      this._links = links;
      this._render();
    } catch (err) {
      this._showError(this._extractMessage(err) || "Impossible de charger le document.");
    }
  }

  _render() {
    const doc = this._document;
    if (!doc) return;

    if (this.hasCrumbTitleTarget) {
      this.crumbTitleTarget.textContent = doc.title || "Document";
    }
    document.title = `Notamicro — Coffre-fort · ${doc.title || "Document"}`;

    const templateId = this._selectTemplate(doc);
    const fragment = this.cloneTemplate(templateId);

    this._fillCommonSlots(fragment, doc);

    if (templateId === "vault-detail-auto-template") {
      this._fillAutoFragment(fragment, doc);
    } else if (templateId === "vault-detail-manual-active-template") {
      this._fillManualActiveFragment(fragment, doc);
    } else {
      this._fillManualArchivedFragment(fragment, doc);
    }

    this.contentTarget.textContent = "";
    this.contentTarget.appendChild(fragment);

    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
    this.contentTarget.hidden = false;
  }

  _selectTemplate(doc) {
    if (doc.isImmutable) return "vault-detail-auto-template";
    if (doc.status === "archived") return "vault-detail-manual-archived-template";
    return "vault-detail-manual-active-template";
  }

  // ---------------------------------------------------------------------------
  // Population — éléments communs aux 3 templates
  // ---------------------------------------------------------------------------

  _fillCommonSlots(fragment, doc) {
    const title = doc.title || "Document";

    const titleEl = fragment.querySelector('[data-slot="title"]');
    if (titleEl) titleEl.textContent = title;

    const metaEl = fragment.querySelector('[data-slot="meta"]');
    if (metaEl) metaEl.textContent = this._buildMetaLine(doc);

    const fileNameEl = fragment.querySelector('[data-slot="fileName"]');
    if (fileNameEl) fileNameEl.textContent = doc.originalFilename || `${title}.pdf`;

    const fileMetaEl = fragment.querySelector('[data-slot="fileMeta"]');
    if (fileMetaEl) fileMetaEl.textContent = this._buildFileMetaLine(doc);

    const downloadEl = fragment.querySelector('[data-slot="downloadLink"]');
    if (downloadEl) {
      downloadEl.setAttribute("href", api.vault.downloadUrl(doc.id));
      downloadEl.setAttribute("aria-label", `Télécharger ${title}`);
    }

    const iframe = fragment.querySelector('[data-vault-detail-target="pdfViewer"]');
    if (iframe) {
      iframe.setAttribute("src", api.vault.downloadUrl(doc.id));
      iframe.setAttribute("title", `Aperçu du document ${title}`);
    }
  }

  _buildMetaLine(doc) {
    const parts = [];
    const date = doc.documentDate || doc.createdAt;
    if (date) {
      parts.push(`${doc.isImmutable ? "Généré" : "Importé"} le ${this._formatDateLong(date)}`);
    }
    if (Number.isFinite(Number(doc.fileSizeBytes))) {
      parts.push(this._formatSize(doc.fileSizeBytes));
    }
    if (doc.mimeType) {
      parts.push(this._humanizeMimeType(doc.mimeType));
    }
    return parts.join(" · ");
  }

  _buildFileMetaLine(doc) {
    const parts = [];
    if (Number.isFinite(Number(doc.fileSizeBytes))) {
      parts.push(this._formatSize(doc.fileSizeBytes));
    }
    if (doc.mimeType) {
      parts.push(this._humanizeMimeType(doc.mimeType));
    }
    return parts.join(" · ");
  }

  // ---------------------------------------------------------------------------
  // Mode AUTO
  // ---------------------------------------------------------------------------

  _fillAutoFragment(fragment, doc) {
    this._setInputValue(fragment, "autoTitle", doc.title || "");
    this._setSelectValue(fragment, "autoCategory", doc.category, this._categoryLabel(doc));
    this._setSelectValue(fragment, "autoFiscalYear", doc.fiscalYear, doc.fiscalYear ?? "");
    this._setInputValue(fragment, "autoDocumentDate", doc.documentDate || "");

    const sourceLink = fragment.querySelector('[data-vault-detail-target="sourcePrimaryLink"]');
    if (!sourceLink) return;

    const sp = this._links?.sourcePrimary;
    if (sp && sp.kind && sp.url) {
      sourceLink.textContent = this._sourcePrimaryLabel(sp.kind, doc.fiscalYear);
      sourceLink.setAttribute("href", sp.url);
      sourceLink.hidden = false;
    } else {
      sourceLink.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Mode MANUAL ACTIVE
  // ---------------------------------------------------------------------------

  _fillManualActiveFragment(fragment, doc) {
    this._setInputValue(fragment, "title", doc.title || "");
    this._setSelectValue(fragment, "category", doc.category, this._categoryLabel(doc));
    this._setSelectValue(fragment, "fiscalYear", doc.fiscalYear, doc.fiscalYear ?? "");
    this._setInputValue(fragment, "documentDate", doc.documentDate || "");
    this._setInputValue(fragment, "notes", doc.notes || "");

    this._originalFormValues = {
      title: doc.title || "",
      category: doc.category || "",
      fiscalYear: doc.fiscalYear == null ? "" : String(doc.fiscalYear),
      documentDate: doc.documentDate || "",
      notes: doc.notes || "",
    };
  }

  // ---------------------------------------------------------------------------
  // Mode MANUAL ARCHIVED
  // ---------------------------------------------------------------------------

  _fillManualArchivedFragment(fragment, doc) {
    this._setInputValue(fragment, "archivedTitle", doc.title || "");
    this._setSelectValue(fragment, "archivedCategory", doc.category, this._categoryLabel(doc));
    this._setSelectValue(fragment, "archivedFiscalYear", doc.fiscalYear, doc.fiscalYear ?? "");
    this._setInputValue(fragment, "archivedDocumentDate", doc.documentDate || "");
    this._setInputValue(fragment, "archivedNotes", doc.notes || "");

    const archivedAtSlot = fragment.querySelector('[data-slot="archivedAt"]');
    if (archivedAtSlot) {
      archivedAtSlot.textContent = doc.archivedAt ? this._formatDateShort(doc.archivedAt) : "—";
    }
  }

  // ---------------------------------------------------------------------------
  // Actions — Mode manual_active
  // ---------------------------------------------------------------------------

  async handleSave(event) {
    event.preventDefault();
    if (!this.hasFormTarget) return;

    const payload = this._collectFormPayload();
    if (!payload) return;

    this._setSaveBusy(true);
    this._setFormHint("");

    try {
      const resp = await api.vault.update(this._id, payload);
      const doc = resp?.data || null;
      if (doc) {
        this._document = doc;
        this._originalFormValues = {
          title: doc.title || "",
          category: doc.category || "",
          fiscalYear: doc.fiscalYear == null ? "" : String(doc.fiscalYear),
          documentDate: doc.documentDate || "",
          notes: doc.notes || "",
        };
        if (this.hasManualActiveTitleTarget) {
          this.manualActiveTitleTarget.textContent = doc.title || "Document";
        }
        if (this.hasCrumbTitleTarget) {
          this.crumbTitleTarget.textContent = doc.title || "Document";
        }
        document.title = `Notamicro — Coffre-fort · ${doc.title || "Document"}`;
      }
      this.showSuccess("Modifications enregistrées.");
      this._setFormHint("Dernier enregistrement à l'instant");
    } catch (err) {
      const message = this._extractMessage(err) || "L'enregistrement a échoué.";
      this.showError(message);
      this._setFormHint(message);
    } finally {
      this._setSaveBusy(false);
    }
  }

  handleCancel() {
    if (!this._originalFormValues) return;
    const o = this._originalFormValues;
    if (this.hasTitleTarget) this.titleTarget.value = o.title;
    if (this.hasCategoryTarget) this.categoryTarget.value = o.category;
    if (this.hasFiscalYearTarget) this.fiscalYearTarget.value = o.fiscalYear;
    if (this.hasDocumentDateTarget) this.documentDateTarget.value = o.documentDate;
    if (this.hasNotesTarget) this.notesTarget.value = o.notes;
    this._setFormHint("Modifications annulées.");
  }

  async handleArchive(event) {
    if (event?.preventDefault) event.preventDefault();

    const ok = await this.confirm(ARCHIVE_WARNING_MESSAGE, {
      title: "Archiver ce document",
      confirmLabel: "Archiver",
      cancelLabel: "Annuler",
    });
    if (!ok) return;

    try {
      await api.vault.archive(this._id);
      this.showSuccess("Document archivé.");
      window.location.href = "/coffre.html";
    } catch (err) {
      this.showError(this._extractMessage(err) || "L'archivage a échoué.");
    }
  }

  _collectFormPayload() {
    const payload = {};

    if (this.hasTitleTarget) {
      const title = (this.titleTarget.value || "").trim();
      if (!title) {
        this.showError("Le titre est obligatoire.");
        this.titleTarget.focus();
        return null;
      }
      payload.title = title;
    }

    if (this.hasCategoryTarget && this.categoryTarget.value) {
      payload.category = this.categoryTarget.value;
    }

    if (this.hasFiscalYearTarget) {
      const fy = (this.fiscalYearTarget.value || "").trim();
      payload.fiscalYear = fy === "" ? null : Number(fy);
    }

    if (this.hasDocumentDateTarget) {
      const dd = (this.documentDateTarget.value || "").trim();
      payload.documentDate = dd === "" ? null : dd;
    }

    if (this.hasNotesTarget) {
      const notes = (this.notesTarget.value || "").trim();
      payload.notes = notes === "" ? null : notes;
    }

    return payload;
  }

  _setSaveBusy(busy) {
    if (!this.hasSaveBtnTarget) return;
    this.saveBtnTarget.disabled = busy;
    this.saveBtnTarget.textContent = busy ? "Enregistrement…" : "Enregistrer";
  }

  _setFormHint(text) {
    if (!this.hasFormTarget) return;
    const hint = this.formTarget.querySelector('[data-slot="formHint"]');
    if (hint) hint.textContent = text || "";
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _setInputValue(fragment, targetName, value) {
    const el = fragment.querySelector(`[data-vault-detail-target="${targetName}"]`);
    if (el) el.value = value;
  }

  _setSelectValue(fragment, targetName, value, humanLabel) {
    const el = fragment.querySelector(`[data-vault-detail-target="${targetName}"]`);
    if (!el) return;
    if (value == null || value === "") {
      el.value = "";
      return;
    }
    const stringValue = String(value);
    const hasOption = Array.from(el.options).some((opt) => opt.value === stringValue);
    if (!hasOption) {
      const opt = document.createElement("option");
      opt.value = stringValue;
      opt.textContent = humanLabel != null && humanLabel !== "" ? String(humanLabel) : stringValue;
      el.prepend(opt);
    }
    el.value = stringValue;
  }

  _categoryLabel(doc) {
    if (!doc.category) return "";
    if (doc.isImmutable) {
      return AUTO_CATEGORY_LABELS[doc.category] || this._humanizeSnake(doc.category);
    }
    return MANUAL_CATEGORY_LABELS[doc.category] || this._humanizeSnake(doc.category);
  }

  _sourcePrimaryLabel(kind, fiscalYear) {
    const fn = SOURCE_PRIMARY_LABELS[kind];
    if (typeof fn === "function") return fn(fiscalYear);
    return "Voir la source";
  }

  _humanizeSnake(value) {
    if (!value || typeof value !== "string") return value ?? "";
    return value
      .split(/[_\s]+/)
      .map((word, idx) => (idx === 0 && word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
      .join(" ");
  }

  _humanizeMimeType(mime) {
    if (!mime || typeof mime !== "string") return "";
    if (mime === "application/pdf") return "PDF";
    if (mime.startsWith("image/")) return mime.slice(6).toUpperCase();
    return mime;
  }

  _formatDateLong(value) {
    if (!value) return "—";
    try {
      return DATE_LONG_FORMATTER.format(new Date(value));
    } catch {
      return value;
    }
  }

  _formatDateShort(value) {
    if (!value) return "—";
    try {
      return DATE_SHORT_FORMATTER.format(new Date(value));
    } catch {
      return value;
    }
  }

  _formatSize(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n < 0) return "—";
    if (n === 0) return "0 o";
    let value = n;
    let unit = 0;
    while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
      value /= 1024;
      unit += 1;
    }
    const formatted = value >= 100 || unit === 0
      ? Math.round(value).toString()
      : value.toFixed(value >= 10 ? 0 : 1).replace(".", ",");
    return `${formatted} ${SIZE_UNITS[unit]}`;
  }

  _showError(message) {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true;
    if (this.hasContentTarget) this.contentTarget.hidden = true;
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
    }
  }

  _extractMessage(err) {
    if (err instanceof APIError) {
      const data = err.data || {};
      if (typeof data.message === "string" && data.message) return data.message;
      if (typeof data.error === "string" && data.error) {
        return this._humanizeErrorCode(data.error);
      }
    }
    if (err && typeof err.message === "string") return err.message;
    return null;
  }

  _humanizeErrorCode(code) {
    switch (code) {
      case "vault_document_not_found":
        return "Document introuvable.";
      case "vault_document_immutable":
        return "Ce document est immuable et ne peut pas être modifié.";
      case "vault_invalid_metadata":
        return "Certaines métadonnées sont invalides.";
      default:
        return "Une erreur est survenue.";
    }
  }
}
